import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Lock, Eye, EyeOff, CheckCircle, MailCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import logger from '../utils/logger';

interface RegisterProps {
    onLoginToggle?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onLoginToggle }) => {
    const { register, isLoading } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        address: '',
        city: '',
        postalCode: '',
        acceptTerms: false
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        
        // Clear register error when user starts typing
        const errorContainer = document.getElementById('register-error-container');
        if (errorContainer && !errorContainer.classList.contains('hidden')) {
            errorContainer.classList.add('hidden');
        }
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Първото име е задължително';
        } else if (formData.firstName.length > 50) {
            newErrors.firstName = 'Първото име не може да бъде по-дълго от 50 символа';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Фамилното име е задължително';
        } else if (formData.lastName.length > 50) {
            newErrors.lastName = 'Фамилното име не може да бъде по-дълго от 50 символа';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Имейлът е задължителен';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Невалиден имейл адрес';
        } else if (formData.email.length > 100) {
            newErrors.email = 'Имейлът не може да бъде по-дълъг от 100 символа';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Телефонът е задължителен';
        } else if (!/^(\+359|0)[0-9]{8,9}$/.test(formData.phone.replace(/\s/g, ''))) {
            newErrors.phone = 'Невалиден телефонен номер. Използвайте формат: +359 888 123 456 или 0888123456';
        }

        if (!formData.password) {
            newErrors.password = 'Паролата е задължителна';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Паролата трябва да бъде поне 6 символа';
        } else if (formData.password.length > 255) {
            newErrors.password = 'Паролата не може да бъде по-дълга от 255 символа';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
            newErrors.password = 'Паролата трябва да съдържа поне една малка буква, една главна буква и една цифра';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Потвърдете паролата';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Паролите не съвпадат';
        }

        if (formData.address && formData.address.length > 255) {
            newErrors.address = 'Адресът не може да бъде по-дълъг от 255 символа';
        }

        if (formData.city && formData.city.length > 100) {
            newErrors.city = 'Градът не може да бъде по-дълъг от 100 символа';
        }

        if (formData.postalCode && !/^[0-9]{4}$/.test(formData.postalCode)) {
            newErrors.postalCode = 'Невалиден пощенски код. Използвайте формат: 1000';
        }

        if (!formData.acceptTerms) {
            newErrors.acceptTerms = 'Трябва да приемете условията';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Hide error before attempting registration
        const errorContainer = document.getElementById('register-error-container');
        const errorText = document.getElementById('register-error-text');
        if (errorContainer) {
            errorContainer.classList.add('hidden');
        }

        try {
            const registerData: any = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phone.replace(/\s/g, ''),
                password: formData.password,
            };

            if (formData.address?.trim()) {
                registerData.address = formData.address.trim();
            }
            if (formData.city?.trim()) {
                registerData.city = formData.city.trim();
            }
            if (formData.postalCode?.trim()) {
                registerData.postalCode = formData.postalCode.trim();
            }

            // Log registration attempt for debugging
            logger.log('🔄 Attempting registration with data:', {
                firstName: registerData.firstName,
                lastName: registerData.lastName,
                email: registerData.email,
                phone: registerData.phone,
                address: registerData.address || 'not provided',
                city: registerData.city || 'not provided',
                postalCode: registerData.postalCode || 'not provided',
                password: '[HIDDEN]'
            });

            const result = await register(registerData);
            logger.log('✅ Registration successful in RegisterPage');
            setSuccess(true);
        } catch (error: any) {
            logger.error('❌ Registration failed in RegisterPage:', error);
            
            if (error.response?.status === 409) {
                setErrors({ 
                    email: 'Потребител с този имейл вече съществува'
                });
                // Show error using DOM manipulation
                setTimeout(() => {
                    const errorContainer = document.getElementById('register-error-container');
                    const errorText = document.getElementById('register-error-text');
                    if (errorContainer && errorText) {
                        errorText.textContent = 'Потребител с този имейл вече съществува. Моля, използвайте друг имейл адрес.';
                        errorContainer.classList.remove('hidden');
                    }
                }, 100);
            } else if (error.response?.status === 400) {
                logger.log('📋 Validation error detected');
                logger.log('📋 Full error response:', error.response.data);
                
                const backendErrors = error.response.data?.message || error.response.data?.errors || error.response.data;
                
                if (Array.isArray(backendErrors)) {
                    logger.log('📝 Processing validation errors:', backendErrors);
                    const newErrors: { [key: string]: string } = {};
                    
                    backendErrors.forEach((msg: string, index: number) => {
                        logger.log(`  ${index + 1}. ${msg}`);
                        
                        if (msg.includes('Първото име') || msg.includes('firstName')) {
                            newErrors.firstName = msg;
                        } else if (msg.includes('Фамилното име') || msg.includes('lastName')) {
                            newErrors.lastName = msg;
                        } else if (msg.includes('Имейлът') || msg.includes('email')) {
                            newErrors.email = msg;
                        } else if (msg.includes('Телефонът') || msg.includes('phone')) {
                            newErrors.phone = msg;
                        } else if (msg.includes('Паролата') || msg.includes('password')) {
                            newErrors.password = msg;
                        } else if (msg.includes('Адресът') || msg.includes('address')) {
                            newErrors.address = msg;
                        } else if (msg.includes('Градът') || msg.includes('city')) {
                            newErrors.city = msg;
                        } else if (msg.includes('Пощенският код') || msg.includes('postalCode')) {
                            newErrors.postalCode = msg;
                        } else {
                            newErrors.submit = msg;
                        }
                    });
                    
                    logger.log('🎯 Mapped errors to form fields:', newErrors);
                    setErrors(newErrors);
                    
                    // Show submit error if exists
                    if (newErrors.submit) {
                        setTimeout(() => {
                            const errorContainer = document.getElementById('register-error-container');
                            const errorText = document.getElementById('register-error-text');
                            if (errorContainer && errorText) {
                                errorText.textContent = newErrors.submit;
                                errorContainer.classList.remove('hidden');
                            }
                        }, 100);
                    }
                } else if (typeof backendErrors === 'string') {
                    logger.log('📄 Single validation error:', backendErrors);
                    setErrors({});
                    setTimeout(() => {
                        const errorContainer = document.getElementById('register-error-container');
                        const errorText = document.getElementById('register-error-text');
                        if (errorContainer && errorText) {
                            errorText.textContent = backendErrors;
                            errorContainer.classList.remove('hidden');
                        }
                    }, 100);
                } else {
                    logger.log('📄 Unknown error format:', backendErrors);
                    setTimeout(() => {
                        const errorContainer = document.getElementById('register-error-container');
                        const errorText = document.getElementById('register-error-text');
                        if (errorContainer && errorText) {
                            errorText.textContent = 'Грешка при валидация на данните';
                            errorContainer.classList.remove('hidden');
                        }
                    }, 100);
                }
            } else {
                logger.log('⚠️ Other registration error:', error.message);
                // Handle other errors (network, server, etc.)
                const errorMessage = error.message || 'Възникна грешка при регистрацията. Моля, опитайте отново.';
                setTimeout(() => {
                    const errorContainer = document.getElementById('register-error-container');
                    const errorText = document.getElementById('register-error-text');
                    if (errorContainer && errorText) {
                        errorText.textContent = errorMessage;
                        errorContainer.classList.remove('hidden');
                    }
                }, 100);
            }
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-6 sm:space-y-8">
                    {/* Лого */}
                    <div className="text-center mb-6 sm:mb-8">
                        <div className="flex items-center justify-center">
                            <Link to='/'>
                                <img
                                    src={require('../logo.png')}
                                    alt="PCCenter Logo"
                                    className="h-14 sm:h-16 w-auto mx-auto transition-transform duration-200 hover:scale-110 object-contain"
                                />
                            </Link>
                        </div>
                    </div>

                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-6">
                            <CheckCircle className="h-12 w-12 text-[#31B823]" />
                            <MailCheck className="h-10 w-10 text-blue-500" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Успешна регистрация!
                        </h2>
                        <p className="text-sm text-gray-700 mb-4">
                            Вашият акаунт беше създаден успешно.
                        </p>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <MailCheck className="mx-auto h-8 w-8 text-blue-600 mb-2" />
                            <h3 className="text-lg font-semibold text-blue-800 mb-2">
                                Потвърдете вашия имейл
                            </h3>
                            <p className="text-sm text-blue-700">
                                Изпратихме ви имейл с линк за потвърждение на вашия имейл адрес. 
                                Моля, проверете входящата си поща и кликнете върху линка, за да завършите регистрацията си.
                            </p>
                        </div>
                        
                        <div className="space-y-3">
                            <Link 
                                to="/profile" 
                                className="block w-full bg-[#31B823] hover:bg-[#28A01D] text-white font-bold py-3 px-4 rounded-lg transition-colors"
                            >
                                Към профила ми
                            </Link>
                            <Link 
                                to="/" 
                                className="block w-full border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 font-medium py-3 px-4 rounded-lg transition-colors"
                            >
                                Към началната страница
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-6 sm:space-y-8">
                {/* Лого */}
                <div className="text-center mb-6 sm:mb-8">
                    <div className="flex items-center justify-center">
                        <Link to='/'>
                            <img
                                src={require('../logo.png')}
                                alt="PCCenter Logo"
                                className="h-14 sm:h-16 w-auto mx-auto transition-transform duration-200 hover:scale-110 object-contain"
                            />
                        </Link>
                    </div>
                </div>

                <div>
                    <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
                        Създайте акаунт
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Или{' '}
                        <button
                            onClick={onLoginToggle}
                            className="font-medium text-[#31B823] hover:text-[#28A01D]"
                        >
                            влезте в съществуващ акаунт
                        </button>
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <div className="space-y-4">
                            {/* Име и фамилия */}
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Име *
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <input
                                            name="firstName"
                                            type="text"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Въведете име"
                                            maxLength={50}
                                        />
                                    </div>
                                    {errors.firstName && (
                                        <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Фамилия *
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <input
                                            name="lastName"
                                            type="text"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Въведете фамилия"
                                            maxLength={50}
                                        />
                                    </div>
                                    {errors.lastName && (
                                        <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                                    )}
                                </div>
                            </div>

                            {/* Имейл */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Имейл адрес *
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.email ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="example@email.com"
                                        maxLength={100}
                                    />
                                </div>
                                {errors.email && (
                                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                )}
                            </div>

                            {/* Телефон */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Телефон *
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.phone ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="+359888123456 или 0888123456"
                                    />
                                </div>
                                {errors.phone && (
                                    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                                )}
                            </div>

                            {/* Парола */}
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Парола *
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <input
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`w-full pl-10 pr-10 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.password ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Минимум 6 символа, малка и главна буква, цифра"
                                            maxLength={255}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Потвърдете паролата *
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                        <input
                                            name="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className={`w-full pl-10 pr-10 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Повторете паролата"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                                    )}
                                </div>
                            </div>

                            {/* Допълнителна информация */}
                            <div className="border-t border-gray-200 pt-4">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">
                                    Допълнителна информация (опционално)
                                </h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Адрес
                                        </label>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                            <input
                                                name="address"
                                                type="text"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.address ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="ул. Иван Вазов 12"
                                                maxLength={255}
                                            />
                                        </div>
                                        {errors.address && (
                                            <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Град
                                            </label>
                                            <input
                                                name="city"
                                                type="text"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.city ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="София"
                                                maxLength={100}
                                            />
                                            {errors.city && (
                                                <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Пощенски код
                                            </label>
                                            <input
                                                name="postalCode"
                                                type="text"
                                                value={formData.postalCode}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] ${errors.postalCode ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="1000"
                                                maxLength={4}
                                            />
                                            {errors.postalCode && (
                                                <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Условия */}
                            <div className="flex items-start">
                                <input
                                    name="acceptTerms"
                                    type="checkbox"
                                    checked={formData.acceptTerms}
                                    onChange={handleInputChange}
                                    className={`mt-1 h-4 w-4 text-[#31B823] border-gray-300 rounded focus:ring-[#31B823] ${errors.acceptTerms ? 'border-red-500' : ''
                                        }`}
                                />
                                <div className="ml-3">
                                    <label className="text-sm text-gray-700">
                                        Съгласен съм с{' '}
                                        <Link to="/terms" className="text-[#31B823] hover:text-[#28A01D]">
                                            общите условия
                                        </Link>{' '}
                                        и{' '}
                                        <Link to="/privacy-policy" className="text-[#31B823] hover:text-[#28A01D]">
                                            политиката за поверителност
                                        </Link>
                                        *
                                    </label>
                                    {errors.acceptTerms && (
                                        <p className="text-red-500 text-xs mt-1">{errors.acceptTerms}</p>
                                    )}
                                </div>
                            </div>

                            {/* Бутон за регистрация */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-[#31B823] hover:bg-[#28A01D] disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded focus:outline-none focus:ring-2 focus:ring-[#31B823] focus:ring-offset-2 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Създаване на акаунт...
                                        </div>
                                    ) : (
                                        'Създай акаунт'
                                    )}
                                </button>
                            </div>
                            
                            {/* Грешка при изпращане */}
                            <div id="register-error-container" className="hidden bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
                                <span id="register-error-text"></span>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;